import React from 'react';

import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {AuditJobsGate, auditJobsModel} from '@entities/audit-jobs/model';
import {JobDictionaryItem, JobExecutionDto, LocalizedTitle} from '@entities/audit-jobs/types';
import {Input} from '@shared/ui/Input';
import {StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';

import {ControlsCard, ResultCell, SearchBox, Wrapper} from './styled';

const localizeTitle = (title: LocalizedTitle | null | undefined, language: string) => {
    if (!title) return EMPTY;
    return title[language] ?? title.en ?? EMPTY;
};

const formatDateTime = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy HH:mm');
};

const statusColor = (code?: string | null) =>
    code === 'FINISHED'
        ? STATUS_COLOR_LIST.GREEN
        : code === 'RUNNING'
        ? STATUS_COLOR_LIST.ORANGE
        : code === 'FAILED'
        ? STATUS_COLOR_LIST.RED
        : STATUS_COLOR_LIST.GRAY_ALT;

const getColumnsConfig = (
    t: (key: string, params?: Record<string, unknown>) => string,
    statusOptions: Array<SelectOption<string>>,
) => [
    {id: 'title', header: t('page_audit.terms.task'), minWidth: 298},
    {
        id: 'version',
        header: t('page_audit.page_jobs.job-template-version'),
        minWidth: 149,
    },
    {id: 'created', header: t('page_audit.terms.started'), minWidth: 120, isSortable: true},
    {id: 'updated', header: t('page_audit.terms.completed'), minWidth: 144, isSortable: true},
    {id: 'result', header: t('page_audit.terms.result'), minWidth: 170},
    {
        id: 'status',
        header: t('page_audit.terms.status'),
        minWidth: 140,
        filter: <Select isClearable options={statusOptions} />,
    },
];

export const AuditJobsTab = () => {
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const jobs = useUnit(auditJobsModel);

    useGate(AuditJobsGate);

    const statusOptions = React.useMemo(
        () =>
            jobs.state.statuses.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language, jobs.state.statuses],
    );

    const data = React.useMemo(
        () =>
            (jobs.state.data?.data ?? []).map((row: JobExecutionDto) => {
                const localizedTitle = localizeTitle(row.jobInfo?.title, i18n.language);
                const title =
                    localizedTitle !== EMPTY
                        ? localizedTitle
                        : localizeTitle(row.jobInfo?.description, i18n.language);
                const status = jobs.state.statuses.find(
                    (item: JobDictionaryItem) =>
                        item.id === row.status?.id || item.code === row.status?.code,
                );
                const statusTitle = localizeTitle(
                    status?.title ?? row.status?.title,
                    i18n.language,
                );
                const created = formatDateTime(row.created);
                const isRunning = row.status?.code === 'RUNNING';
                const hasResult =
                    row.result?.violationsClosed != null ||
                    row.result?.violationsOpened != null ||
                    row.result?.notificationsForViolationsClosed != null ||
                    row.result?.notificationsForViolationsOpened != null;
                const closedCount =
                    row.result?.violationsClosed ??
                    row.result?.notificationsForViolationsClosed ??
                    0;
                const openedCount =
                    row.result?.violationsOpened ??
                    row.result?.notificationsForViolationsOpened ??
                    0;

                return {
                    ...row,
                    title: t('page_audit.terms.job-name-from', {
                        name: title,
                        dateTime: created,
                    }),
                    version: row.jobInfo?.version ?? EMPTY,
                    created,
                    updated: isRunning ? EMPTY : formatDateTime(row.updated),
                    result: hasResult ? (
                        <ResultCell>
                            <span>
                                {t('page_audit.page_jobs.violations-closed', {
                                    count: closedCount,
                                })}
                            </span>
                            <span>
                                {t('page_audit.page_jobs.violations-opened', {
                                    count: openedCount,
                                })}
                            </span>
                        </ResultCell>
                    ) : (
                        EMPTY
                    ),
                    status: (
                        <StatusWrapper color={statusColor(row.status?.code)}>
                            {statusTitle}
                        </StatusWrapper>
                    ),
                };
            }),
        [i18n.language, jobs.state.data, jobs.state.statuses, t],
    );

    const columns = React.useMemo(
        () =>
            mapTableColumns(
                getColumnsConfig(t, statusOptions),
                jobs.state.filters,
                jobs.filtersChanged,
                jobs.filtersReset,
                jobs.state.sortings,
                jobs.sortingsChanged,
            ),
        [
            jobs.filtersChanged,
            jobs.filtersReset,
            jobs.sortingsChanged,
            jobs.state.filters,
            jobs.state.sortings,
            statusOptions,
            t,
        ],
    );

    return (
        <Wrapper>
            <ControlsCard>
                <SearchBox>
                    <Input
                        size="sm"
                        value={jobs.state.search}
                        placeholder={t('page_audit.page_jobs.search_placeholder')}
                        onChange={(event) => jobs.searchChanged(event.target.value)}
                    />
                </SearchBox>
            </ControlsCard>
            <Table
                columns={columns}
                data={data}
                isLoading={jobs.state.pending}
                pageCount={Math.ceil((jobs.state.data?.total ?? 0) / jobs.state.pageSize)}
                pageIndex={jobs.state.pageNumber}
                totalRowsCount={jobs.state.data?.total ?? 0}
                onPageChange={jobs.pageNumberChanged}
                onPageSizeChange={jobs.pageSizeChanged}
                onRowClick={(row) => {
                    const jobInfoId = row.original.jobInfo?.id;
                    if (!jobInfoId) return;

                    history.push(
                        ROUTES.PREFERENCES_JOB_TEMPLATE_DETAILS.replace(
                            ':jobTemplateId',
                            jobInfoId,
                        ),
                    );
                }}
            />
        </Wrapper>
    );
};
